import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Star, Search, Plus } from 'lucide-react';
import { getAgGridSortStorageKey } from '../../../../components/UI/AgGrid';
import { useUserContext } from '../../../../contexts/UserContext';
import { updateSpecificGithubIntegration } from '../../../../utils/APIs';
import { formatDate } from '../../../../utils/converter';
import { toast } from 'react-toastify';
import Loader from '../../../../components/UI/Loader/Loader';
import DeleteIntegrationModal from './DeleteIntegrationModal';
import { Card, Button, Input, AgTableCard } from '../../../../components/UI';

const GithubIntegration = () => {
    const navigate = useNavigate();
    const { userData, updateGithubIntegrations } = useUserContext();
    const [deleteIntegration, setDeleteIntegration] = useState("");
    const [data, setData] = useState([]);
    const [loading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [handleId, setHandleId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setData(userData?.githubIntegrations || []);
    }, [userData?.githubIntegrations]);

    const [gridApi, setGridApi] = useState(null);

    const onFilterTextBoxChanged = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (gridApi) {
            gridApi.setFilterModel({
                name: val ? { type: 'contains', filter: val.toLowerCase() } : null
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            gridApi.setFilterModel({});
        }
    };

    const handleSetAsDefault = async (id) => {
        try {
            setActionLoading(true);
            const res = await updateSpecificGithubIntegration(
                userData.token,
                userData.selectedOrganization.org_token,
                { integrationId: id, bodyData: { default: true } }
            );
            updateGithubIntegrations();
            toast.success(res?.message || "Set as default successfully");
        } catch (error) {
            toast.error(error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const colDefs = [
        {
            headerName: "Name",
            field: "name",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-primary hover:text-primary-hover hover:underline text-left"
                    onClick={() => navigate(`/setup/edit-github-integration/${data._id}`)}
                >
                    {data.name}
                </button>
            ),
            filter: true,
            sortable: true,
            suppressMovable: true,
        },
        {
            headerName: "Repo Name",
            field: "repo_name",
            sortable: true,
            cellRenderer: ({ value }) => (
                <span className="font-mono text-xs text-stride-muted">{value || "—"}</span>
            ),
            suppressMovable: true
        },
        {
            headerName: "Repo Owner",
            field: "repo_owner_username",
            sortable: true,
            cellRenderer: ({ value }) => (
                <span className="text-stride-body text-xs">{value || "—"}</span>
            ),
            suppressMovable: true
        },
        {
            headerName: "Default",
            field: "default",
            sortable: false,
            cellRenderer: ({ data }) => {
                const isDefault = data.default === true || data.default === "true";
                return isDefault ? (
                    <span className="stride-badge-pill bg-emerald-50 text-emerald-700 font-semibold text-xs">
                        Default
                    </span>
                ) : (
                    <span className="text-stride-muted text-xs">—</span>
                );
            },
            suppressMovable: true
        },
        {
            headerName: "Updated At",
            field: "updatedAt",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.updatedAt)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ({ data }) => {
                const isDefault = data.default === true || data.default === "true";
                return (
                    <div className="stride-table-actions">
                        <button
                            type="button"
                            title="Edit Integration"
                            className="stride-table-btn stride-table-btn-edit"
                            onClick={() => navigate(`/setup/edit-github-integration/${data._id}`)}
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {!isDefault && (
                            <button
                                type="button"
                                title="Set as default"
                                className="stride-table-btn hover:text-amber-600 hover:border-amber-300"
                                onClick={() => handleSetAsDefault(data._id)}
                            >
                                <Star className="w-3.5 h-3.5 text-amber-500" />
                            </button>
                        )}
                        <button
                            type="button"
                            title="Delete Integration"
                            className="stride-table-btn stride-table-btn-delete"
                            onClick={() => {
                                setHandleId(data._id);
                                setDeleteIntegration(data._id);
                            }}
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                );
            },
            width: 100,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true
        },
    ];

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("github-integration-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [{ colId: 'updatedAt', sort: 'desc' }],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                { colId: "name", minWidth: 240 },
                { colId: "repo_name", minWidth: 180 },
                { colId: "repo_owner_username", minWidth: 160 },
                { colId: "default", minWidth: 120 },
                { colId: "updatedAt", minWidth: 160 },
                { colId: "actions", minWidth: 90 },
            ],
        };
    }, []);

    return (
        <main id="main" className="stride-page-shell flex flex-col space-y-6">
            {/* Header */}
            <div>
                <span className="stride-eyebrow">Setup / Integrations</span>
                <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                    GitHub Integrations
                </h1>
                <p className="text-sm text-stride-muted mt-1">
                    Manage connected GitHub repositories and webhooks for code deployments.
                </p>
            </div>

            {/* Empty State */}
            {!loading && (!data || data.length === 0) ? (
                <Card className="text-center p-12 max-w-lg mx-auto">
                    <p className="text-base text-stride-heading font-medium mb-4">
                        GitHub Integration not found, please create a new integration.
                    </p>
                    <Button
                        variant="primary"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => navigate('/setup/add-github-integration')}
                    >
                        Create New GitHub Integration
                    </Button>
                </Card>
            ) : (
                <AgTableCard
                    header={
                        <>
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder="Search integration name..."
                                    value={searchTerm}
                                    onChange={onFilterTextBoxChanged}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={handleClearSearch}
                                />
                            </div>

                            <Button
                                variant="primary"
                                icon={<Plus className="w-4 h-4" />}
                                onClick={() => navigate('/setup/add-github-integration')}
                            >
                                New GitHub Integration
                            </Button>
                        </>
                    }
                    tableId="github-integration-table"
                    loading={loading}
                    rowData={data}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                />
            )}

            {actionLoading && <Loader />}

            {deleteIntegration && (
                <DeleteIntegrationModal
                    id={handleId}
                    setDeleteIntegration={setDeleteIntegration}
                    setActionLoading={setActionLoading}
                    getGithubData={updateGithubIntegrations}
                />
            )}
        </main>
    );
};

export default GithubIntegration;
